import jsPDF from 'jspdf';
import 'jspdf-autotable';

// jspdf-autotable automatically extends jsPDF with the autoTable method

// Helper function to format numbers in Indian style (₹1,00,000)
const formatIndianNumber = (num) => {
  const numStr = num.toString();
  const lastThree = numStr.substring(numStr.length - 3);
  const otherNumbers = numStr.substring(0, numStr.length - 3);
  if (otherNumbers !== '') {
    return otherNumbers.replace(/\B(?=(\d{2})+(?!\d))/g, ',') + ',' + lastThree;
  }
  return lastThree;
};

// HSN Codes for different materials
const HSN_CODES = {
  gold: '710812',
  diamond: '710239',
  stone: '710399',
  colour_stone: '710399',
  other: '999999',
};

export const generateDeliveryChallanPDF = (challan) => {
  try {
    console.log('Generating PDF for challan:', challan);

    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    let yPos = 15;

  // ============= HEADER =============
  // Title
  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  doc.text('DELIVERY CHALLAN', pageWidth / 2, yPos, { align: 'center' });
  yPos += 8;

  // Rule reference
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text('(Issued under Rule 55 of CGST Rules, 2017)', pageWidth / 2, yPos, { align: 'center' });
  yPos += 10;

  // Purpose statement
  doc.setFontSize(8);
  doc.setFont('helvetica', 'italic');
  const purposeText = 'Goods sent for Job Work under Section 143 of CGST Act, 2017. Ownership of goods retained by the Principal and goods shall be returned after processing.';
  const splitPurpose = doc.splitTextToSize(purposeText, pageWidth - 30);
  doc.text(splitPurpose, 15, yPos);
  yPos += splitPurpose.length * 4 + 5;

  // ============= SENDER & RECEIVER DETAILS =============
  const boxStartY = yPos;
  const boxHeight = 35;
  const halfWidth = (pageWidth - 30) / 2;

  // Draw boxes
  doc.rect(15, boxStartY, halfWidth, boxHeight);
  doc.rect(15 + halfWidth, boxStartY, halfWidth, boxHeight);

  // SENDER (Principal)
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.text('SENDER (PRINCIPAL):', 18, boxStartY + 5);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.text('Name: HMJ', 18, boxStartY + 10);
  doc.text('Address: 470 J hills hyd', 18, boxStartY + 15);
  doc.text('Contact No: 9676498891', 18, boxStartY + 20);
  doc.text('GSTIN: N/A', 18, boxStartY + 25);

  // RECEIVER (Job Worker)
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.text('RECEIVER (JOB WORKER):', 18 + halfWidth, boxStartY + 5);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.text(`Name: ${challan.worker_name}`, 18 + halfWidth, boxStartY + 10);
  doc.text(`Address: -`, 18 + halfWidth, boxStartY + 15);
  doc.text(`Contact No: ${challan.worker_mobile || 'N/A'}`, 18 + halfWidth, boxStartY + 20);
  doc.text('GSTIN: N/A', 18 + halfWidth, boxStartY + 25);

  yPos = boxStartY + boxHeight + 10;

  // ============= CHALLAN DETAILS =============
  const detailsStartY = yPos;
  const col1X = 15;
  const col2X = 110;  // Moved right to avoid overlap
  const col3X = 155;

  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.text('Delivery Challan No:', col1X, detailsStartY);
  doc.setFont('helvetica', 'normal');
  doc.text(challan.challan_number, col1X + 42, detailsStartY);

  doc.setFont('helvetica', 'bold');
  doc.text('Date:', col2X, detailsStartY);
  doc.setFont('helvetica', 'normal');
  doc.text(new Date(challan.created_at).toLocaleDateString('en-IN'), col2X + 12, detailsStartY);

  yPos = detailsStartY + 7;

  // Transport details
  const transport = challan.transport_details;
  doc.setFont('helvetica', 'bold');
  doc.text('Transporter Name:', col1X, yPos);
  doc.setFont('helvetica', 'normal');
  doc.text(transport.transporter_name || '-', col1X + 42, yPos);

  if (transport.vehicle_number) {
    doc.setFont('helvetica', 'bold');
    doc.text('Vehicle No:', col2X, yPos);
    doc.setFont('helvetica', 'normal');
    doc.text(transport.vehicle_number, col2X + 24, yPos);
  }

  yPos += 7;

  if (transport.lr_awb_number) {
    doc.setFont('helvetica', 'bold');
    doc.text('LR / AWB No:', col1X, yPos);
    doc.setFont('helvetica', 'normal');
    doc.text(transport.lr_awb_number, col1X + 30, yPos);
  }

  if (transport.eway_bill_number) {
    doc.setFont('helvetica', 'bold');
    doc.text('E-Way Bill No:', col2X, yPos);
    doc.setFont('helvetica', 'normal');
    doc.text(transport.eway_bill_number, col2X + 30, yPos);
  }

  yPos += 10;

  // ============= MATERIALS TABLE =============
  const tableData = challan.material_requests_data.map((mat) => {
    let description = mat.material_type.charAt(0).toUpperCase() + mat.material_type.slice(1);
    if (mat.metal_purity) description += ` (${mat.metal_purity})`;

    const hsnCode = HSN_CODES[mat.material_type] || HSN_CODES.other;

    let weightQty = '';
    if (mat.weight_grams) weightQty = `${mat.weight_grams}g`;
    if (mat.weight_carats) weightQty = `${mat.weight_carats}ct`;

    // Format value without spaces - Indian number format
    const approxValue = mat.insurance_value
      ? `₹${formatIndianNumber(Math.round(mat.insurance_value))}`
      : '-';

    return [description, hsnCode, weightQty, approxValue];
  });

  doc.autoTable({
    startY: yPos,
    head: [['Description of Goods', 'HSN Code', 'Weight / Qty', 'Approximate Value']],
    body: tableData,
    theme: 'grid',
    headStyles: {
      fillColor: [15, 118, 110],
      textColor: 255,
      fontSize: 9,
      fontStyle: 'bold',
      halign: 'center',
    },
    bodyStyles: {
      fontSize: 9,
    },
    columnStyles: {
      0: { cellWidth: 70 },
      1: { cellWidth: 30, halign: 'center' },
      2: { cellWidth: 35, halign: 'center' },
      3: { cellWidth: 45, halign: 'right' },
    },
  });

  yPos = doc.lastAutoTable.finalY + 10;

  // ============= TOTAL =============
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('Total Approximate Value:', pageWidth - 80, yPos);
  doc.text(`₹${formatIndianNumber(Math.round(challan.total_approximate_value))}`, pageWidth - 15, yPos, { align: 'right' });

  yPos += 7;
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  const totalInWords = `(In Words: Rupees ${numberToWords(Math.round(challan.total_approximate_value))} Only)`;
  doc.text(totalInWords, 15, yPos);

  yPos += 10;

  // ============= FOOTER =============
  // GST Statement
  doc.setFontSize(8);
  doc.setFont('helvetica', 'italic');
  const gstText = 'GST is Not Applicable as the movement is for Job Work under Section 143 of CGST Act. Value shown is approximate for identification and insurance purposes only.';
  const splitGst = doc.splitTextToSize(gstText, pageWidth - 30);
  doc.text(splitGst, 15, yPos);
  yPos += splitGst.length * 4 + 10;

  // Signature boxes
  const sigBoxY = Math.max(yPos, pageHeight - 45);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.text('Sender Signature & Stamp:', 20, sigBoxY);
  doc.text('Receiver Signature & Stamp:', pageWidth / 2 + 10, sigBoxY);

  doc.line(15, sigBoxY + 15, pageWidth / 2 - 5, sigBoxY + 15);
  doc.line(pageWidth / 2 + 5, sigBoxY + 15, pageWidth - 15, sigBoxY + 15);

  // ============= SAVE PDF =============
  doc.save(`Delivery-Challan-${challan.challan_number}.pdf`);

  } catch (error) {
    console.error('Error generating PDF:', error);
    throw new Error(`Failed to generate PDF: ${error.message}`);
  }
};

// Helper function to convert number to words (Indian system)
const numberToWords = (num) => {
  if (num === 0) return 'Zero';

  const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
  const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];

  const convertTwoDigit = (n) => {
    if (n < 10) return ones[n];
    if (n >= 10 && n < 20) return teens[n - 10];
    return tens[Math.floor(n / 10)] + (n % 10 !== 0 ? ' ' + ones[n % 10] : '');
  };

  const convertThreeDigit = (n) => {
    if (n === 0) return '';
    if (n < 100) return convertTwoDigit(n);
    return ones[Math.floor(n / 100)] + ' Hundred' + (n % 100 !== 0 ? ' ' + convertTwoDigit(n % 100) : '');
  };

  // Indian numbering system: Ones, Thousands, Lakhs, Crores
  if (num < 1000) {
    return convertThreeDigit(num);
  } else if (num < 100000) {
    const thousands = Math.floor(num / 1000);
    const remainder = num % 1000;
    return convertTwoDigit(thousands) + ' Thousand' + (remainder !== 0 ? ' ' + convertThreeDigit(remainder) : '');
  } else if (num < 10000000) {
    const lakhs = Math.floor(num / 100000);
    const remainder = num % 100000;
    return convertTwoDigit(lakhs) + ' Lakh' + (remainder !== 0 ? ' ' + numberToWords(remainder) : '');
  } else {
    const crores = Math.floor(num / 10000000);
    const remainder = num % 10000000;
    return convertTwoDigit(crores) + ' Crore' + (remainder !== 0 ? ' ' + numberToWords(remainder) : '');
  }
};
